<template>
  <div class="personal-requests-page">
    <div class="personal-requests-shell">
      <!-- Hero -->
      <section class="page-hero">
        <div class="page-hero__copy">
          <div class="page-hero__icon" aria-hidden="true">
            <v-icon color="primary">mdi-account-question</v-icon>
          </div>
          <div>
            <div class="app-eyebrow">People Operations</div>
            <h1>Personalanliegen</h1>
            <p>Anliegen strukturiert erfassen, gemeinsam bearbeiten und sicher visieren.</p>
          </div>
        </div>
        <v-btn color="primary" depressed class="hero-cta" @click="openCreateDialog">
          <v-icon left>mdi-plus</v-icon>
          Neues Anliegen
        </v-btn>
      </section>

      <!-- Übersicht -->
      <v-row class="summary-grid" dense>
        <v-col cols="12" sm="4">
          <div class="summary-card">
            <div class="summary-card__icon summary-card__icon--primary">
              <v-icon>mdi-format-list-bulleted</v-icon>
            </div>
            <div>
              <span class="summary-card__label">Aktuelle Auswahl</span>
              <strong>{{ loading ? "–" : requests.length }}</strong>
              <small>Anliegen</small>
            </div>
          </div>
        </v-col>
        <v-col cols="12" sm="4">
          <div class="summary-card">
            <div class="summary-card__icon summary-card__icon--priority">
              <v-icon>mdi-flag-variant</v-icon>
            </div>
            <div>
              <span class="summary-card__label">Hohe Priorität</span>
              <strong>{{ loading ? "–" : highPriorityCount }}</strong>
              <small>in dieser Auswahl</small>
            </div>
          </div>
        </v-col>
        <v-col cols="12" sm="4">
          <div class="summary-card">
            <div class="summary-card__icon summary-card__icon--success">
              <v-icon>mdi-check-decagram</v-icon>
            </div>
            <div>
              <span class="summary-card__label">Vollständig visiert</span>
              <strong>{{ loading ? "–" : fullySignedCount }}</strong>
              <small>bereit dokumentiert</small>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Filter -->
      <section class="filter-surface">
        <div class="surface-heading">
          <div>
            <div class="app-eyebrow">Ansicht</div>
            <h2>Filter &amp; Ergebnisse</h2>
          </div>
          <div class="surface-heading__meta">
            <span class="result-count">
              {{ loading ? "Wird geladen …" : requests.length + " " + (requests.length === 1 ? "Ergebnis" : "Ergebnisse") }}
            </span>
            <v-btn text small class="reset-button" :disabled="!hasActiveFilters" @click="resetFilters">
              <v-icon left small>mdi-filter-off-outline</v-icon>
              Zurücksetzen
            </v-btn>
          </div>
        </div>
        <v-row dense class="filter-grid">
          <v-col v-if="canManage" cols="12" sm="6" md="4">
            <v-select
              v-model="filterStudio"
              :items="studioFilterOptions"
              item-text="name"
              item-value="_id"
              label="Studio"
              prepend-inner-icon="mdi-map-marker-outline"
              outlined
              dense
              dark
              hide-details
              @change="loadRequests" />
          </v-col>
          <v-col cols="12" sm="6" :md="canManage ? 4 : 6">
            <v-select
              v-model="filterStatus"
              :items="statusFilterOptions"
              label="Status"
              prepend-inner-icon="mdi-progress-check"
              outlined
              dense
              dark
              hide-details
              @change="loadRequests" />
          </v-col>
          <v-col cols="12" sm="6" :md="canManage ? 4 : 6">
            <v-select
              v-model="filterPriority"
              :items="priorityFilterOptions"
              label="Priorität"
              prepend-inner-icon="mdi-flag-outline"
              outlined
              dense
              dark
              hide-details
              @change="loadRequests" />
          </v-col>
        </v-row>
      </section>

      <!-- Desktop-Liste -->
      <section class="requests-surface d-none d-lg-block">
        <v-data-table
          dark
          :headers="tableHeaders"
          :items="requests"
          :loading="loading"
          :items-per-page="25"
          class="requests-table"
          no-data-text="Keine Personalanliegen vorhanden"
          @click:row="openDetailDialog">
          <template v-slot:item.createdAt="{ item }">
            <span class="table-date">{{ $moment(item.createdAt).format("DD.MM.YYYY") }}</span>
          </template>
          <template v-slot:item.title="{ item }">
            <div class="table-title-cell">
              <strong>{{ item.title }}</strong>
            </div>
          </template>
          <template v-slot:item.affectedEmployee="{ item }">
            <span class="table-person">
              <v-icon small>mdi-account-outline</v-icon>
              {{ getAffectedPersonName(item) }}
              <small v-if="item.isFutureEmployee" class="future-person-badge">Noch kein MA</small>
            </span>
          </template>
          <template v-slot:item.studio="{ item }">
            {{ item.studio ? item.studio.name : "-" }}
          </template>
          <template v-slot:item.priority="{ item }">
            <v-chip small :color="priorityColor(item.priority)" dark class="status-pill">
              <span class="status-pill__dot"></span>
              {{ priorityLabel(item.priority) }}
            </v-chip>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip small :color="statusColor(item.status)" dark class="status-pill">
              {{ statusLabel(item.status) }}
            </v-chip>
          </template>
          <template v-slot:item.signatures="{ item }">
            <div class="signature-cell">
              <div class="signature-cell__label">
                <v-icon x-small :color="item.fullySigned ? '#55ca82' : '#a3a3a8'">
                  {{ item.fullySigned ? "mdi-check-all" : "mdi-progress-check" }}
                </v-icon>
                <span>{{ signatureCount(item) }}/{{ requiredSignerCount(item) }}</span>
              </div>
              <v-progress-linear
                rounded
                height="4"
                :value="signatureProgressValue(item)"
                :color="item.fullySigned ? '#55ca82' : '#009640'"
                background-color="rgba(255, 255, 255, 0.1)" />
            </div>
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="table-actions">
              <v-btn icon small class="touch-action" title="PDF herunterladen" aria-label="PDF herunterladen" @click.stop="downloadPdf(item)">
                <v-icon small>mdi-file-pdf-box</v-icon>
              </v-btn>
              <v-btn icon small class="touch-action" title="Details öffnen" aria-label="Details öffnen" @click.stop="openDetailDialog(item)">
                <v-icon small>mdi-arrow-right</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </section>

      <!-- Mobile-Liste -->
      <section class="mobile-requests d-lg-none">
        <v-data-iterator
          dark
          :items="requests"
          :items-per-page="25"
          :loading="loading"
          item-key="_id"
          class="mobile-iterator"
          :footer-props="{ 'items-per-page-options': [25], 'items-per-page-text': 'Pro Seite' }">
          <template v-slot:default="{ items }">
            <div class="mobile-request-grid">
              <article
                v-for="item in items"
                :key="item._id"
                class="request-card"
                role="button"
                tabindex="0"
                @click="openDetailDialog(item)"
                @keyup.enter.self="openDetailDialog(item)"
                @keydown.space.prevent.self="openDetailDialog(item)">
                <div class="request-card__top">
                  <span class="table-date">{{ $moment(item.createdAt).format("DD.MM.YYYY") }}</span>
                  <div class="request-card__pills">
                    <v-chip small :color="priorityColor(item.priority)" dark class="status-pill">
                      {{ priorityLabel(item.priority) }}
                    </v-chip>
                    <v-chip small :color="statusColor(item.status)" dark class="status-pill">
                      {{ statusLabel(item.status) }}
                    </v-chip>
                  </div>
                </div>
                <div class="request-card__body">
                  <span class="request-card__category">{{ item.category || "Ohne Kategorie" }}</span>
                  <h3>{{ item.title }}</h3>
                  <div class="request-card__meta">
                    <span>
                      <v-icon small>mdi-account-outline</v-icon>
                      {{ getAffectedPersonName(item) }}
                      <small v-if="item.isFutureEmployee" class="future-person-badge">Noch kein MA</small>
                    </span>
                    <span>
                      <v-icon small>mdi-map-marker-outline</v-icon>
                      {{ item.studio ? item.studio.name : "-" }}
                    </span>
                  </div>
                  <div class="request-card__signatures">
                    <div>
                      <span>Visierungen</span>
                      <strong>{{ signatureCount(item) }}/{{ requiredSignerCount(item) }}</strong>
                    </div>
                    <v-progress-linear
                      rounded
                      height="6"
                      :value="signatureProgressValue(item)"
                      :color="item.fullySigned ? '#55ca82' : '#009640'"
                      background-color="rgba(255, 255, 255, 0.1)" />
                  </div>
                </div>
                <div class="request-card__actions">
                  <v-btn text small class="card-action" @click.stop="downloadPdf(item)">
                    <v-icon left small>mdi-file-pdf-box</v-icon>
                    PDF
                  </v-btn>
                  <v-btn text small color="primary" class="card-action" @click.stop="openDetailDialog(item)">
                    Details
                    <v-icon right small>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
              </article>
            </div>
          </template>
          <template v-slot:no-data>
            <div class="empty-state">
              <div class="empty-state__icon"><v-icon>mdi-inbox-outline</v-icon></div>
              <h3>Keine Personalanliegen</h3>
              <p>Für die aktuelle Filterauswahl wurden keine Anliegen gefunden.</p>
            </div>
          </template>
        </v-data-iterator>
      </section>

      <!-- CREATE / EDIT DIALOG -->
      <v-dialog
        :fullscreen="$vuetify.breakpoint.smAndDown"
        v-model="createDialog"
        max-width="880"
        persistent
        scrollable
        content-class="personal-request-dialog">
        <v-card dark class="app-dialog-card">
          <v-card-title class="dialog-header">
            <div class="dialog-header__copy">
              <div class="dialog-header__icon">
                <v-icon>{{ isEditMode ? "mdi-pencil-outline" : "mdi-plus" }}</v-icon>
              </div>
              <div>
                <div class="app-eyebrow">{{ isEditMode ? "Anliegen aktualisieren" : "Neue Anfrage" }}</div>
                <h2>{{ isEditMode ? "Personalanliegen bearbeiten" : "Neues Personalanliegen" }}</h2>
                <p>{{ isEditMode ? "Angaben prüfen und Änderungen sicher speichern." : "Alle relevanten Angaben für HR vollständig erfassen." }}</p>
              </div>
            </div>
            <v-btn
              icon
              class="dialog-close touch-action"
              :disabled="saving"
              title="Dialog schliessen"
              aria-label="Dialog schliessen"
              @click="closeCreateDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider class="app-divider"></v-divider>
          <v-card-text class="dialog-scroll-area">
            <v-form ref="createForm" v-model="formValid" class="form-stack">
              <section class="form-section">
                <div class="section-heading">
                  <div class="section-heading__icon"><v-icon small>mdi-account-details-outline</v-icon></div>
                  <div>
                    <h3>Allgemein</h3>
                    <p>Zuordnung, Priorität und betroffene Person.</p>
                  </div>
                </div>
                <v-row dense>
                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="form.studio"
                      :items="studioOptions"
                      item-text="name"
                      item-value="_id"
                      label="Studio *"
                      outlined
                      dense
                      dark
                      :disabled="!canManage && !isEditMode"
                      :hint="!canManage ? 'Dein angemeldetes Studio' : ''"
                      persistent-hint
                      :rules="[(v) => !!v || 'Pflichtfeld']" />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-select v-model="form.priority" :items="priorityOptions" label="Priorität" outlined dense dark />
                  </v-col>

                  <v-col cols="12">
                    <div class="person-mode-card">
                      <v-switch
                        v-model="form.isFutureEmployee"
                        class="person-mode-switch"
                        color="primary"
                        inset
                        hide-details
                        label="Person ist noch kein Mitarbeitender bei uns"
                        @change="onFutureEmployeeChange" />
                      <p>Für zukünftige Mitarbeitende, die noch keinen Roody-Account haben.</p>
                    </div>
                  </v-col>

                  <v-col v-if="form.isFutureEmployee" cols="12">
                    <v-text-field
                      v-model="form.affectedPersonName"
                      label="Vor- und Nachname der Person *"
                      placeholder="z.B. Max Muster"
                      prepend-inner-icon="mdi-account-plus-outline"
                      maxlength="120"
                      outlined
                      dense
                      dark
                      :rules="[(v) => !!(v && v.trim()) || 'Pflichtfeld']" />
                  </v-col>

                  <v-col v-else cols="12">
                    <v-subheader class="pl-0">Betroffener Mitarbeitender *</v-subheader>
                    <user-search
                      :value="form.affectedEmployee"
                      ignoreRights
                      instructor
                      :key="'emp-' + (form.affectedEmployee || 'none')"
                      label="Mitarbeiter suchen..."
                      @update="updateAffectedEmployee"></user-search>
                    <div v-if="!form.affectedEmployee" class="red--text text--lighten-2 caption mt-1">Pflichtfeld</div>
                  </v-col>

                  <v-col cols="12">
                    <v-combobox
                      v-model="form.category"
                      :items="categoryOptions"
                      :search-input.sync="categorySearch"
                      label="Kategorie *"
                      outlined
                      dense
                      dark
                      hide-no-data
                      :rules="[(v) => !!v || 'Pflichtfeld']"
                      placeholder="z.B. Stellenprozent-Anpassung, Arbeitsortwechsell..."
                      :loading="loadingCategories">
                      <template v-slot:no-data>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>
                              Neue Kategorie "<strong>{{ categorySearch }}</strong
                              >" wird angelegt
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                    </v-combobox>
                  </v-col>

                  <v-col cols="12">
                    <v-text-field
                      v-model="form.title"
                      label="Titel *"
                      placeholder="Kurze Zusammenfassung"
                      outlined
                      dense
                      dark
                      :rules="[(v) => !!v || 'Pflichtfeld']" />
                  </v-col>

                  <v-col cols="12" sm="6">
                    <v-text-field v-model="form.requestedEffectiveDate" label="Gewünschtes Umsetzungsdatum" type="date" outlined dense dark />
                  </v-col>
                </v-row>
              </section>

              <section class="form-section">
                <div class="section-heading">
                  <div class="section-heading__icon"><v-icon small>mdi-text-box-outline</v-icon></div>
                  <div>
                    <h3>Beschreibung</h3>
                    <p>Kontext und gewünschte Änderung verständlich festhalten.</p>
                  </div>
                </div>
                <v-row dense>
                  <v-col cols="12">
                    <v-textarea
                      v-model="form.description"
                      label="Beschreibung *"
                      outlined
                      dense
                      dark
                      rows="5"
                      placeholder="Beschreibe das Anliegen ausführlich (Hintergrund, aktuelle Situation, gewünschte Änderung)..."
                      :rules="[(v) => !!v || 'Pflichtfeld']" />
                  </v-col>
                  <v-col cols="12">
                    <v-textarea
                      v-model="form.creatorComment"
                      label="Zusätzlicher Kommentar (optional)"
                      outlined
                      dense
                      dark
                      rows="2"
                      placeholder="Anmerkungen, Empfehlung, Begründung..." />
                  </v-col>
                </v-row>
              </section>

              <!-- Lohnanfrage-Spezialfelder -->
              <template v-if="features.salaryRequestsEnabled && isSalaryRequest && form.salaryDetails">
                <section class="form-section form-section--accent">
                  <div class="section-heading">
                    <div class="section-heading__icon"><v-icon small>mdi-cash-multiple</v-icon></div>
                    <div>
                      <h3>Lohnanfrage – Zusatzangaben</h3>
                      <p>Lohnentwicklung, Erfahrung und Zielerreichung ergänzen.</p>
                    </div>
                  </div>
                  <v-row dense>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="form.salaryDetails.ausbildung"
                        label="Ausbildung *"
                        outlined
                        dense
                        dark
                        :rules="[(v) => !!v || 'Pflichtfeld']" />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="form.salaryDetails.branchenErfahrung"
                        label="Branchen-Erfahrung *"
                        placeholder="z.B. 5 Jahre Fitnessbranche"
                        outlined
                        dense
                        dark
                        :rules="[(v) => !!v || 'Pflichtfeld']" />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="form.salaryDetails.dauerBeiWellcomeFit"
                        label="Dauer bei well come FIT *"
                        placeholder="z.B. 3 Jahre 4 Monate"
                        outlined
                        dense
                        dark
                        :rules="[(v) => !!v || 'Pflichtfeld']" />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model.number="form.salaryDetails.anfangslohn"
                        label="Anfangslohn (CHF) *"
                        type="number"
                        prefix="CHF"
                        outlined
                        dense
                        dark
                        :rules="[(v) => (v != null && v !== '') || 'Pflichtfeld']" />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="form.salaryDetails.letzteLohnanpassung.datum"
                        label="Letzte Lohnanpassung (Datum)"
                        type="date"
                        outlined
                        dense
                        dark />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model.number="form.salaryDetails.letzteLohnanpassung.betrag"
                        label="Letzte Lohnanpassung (Betrag)"
                        type="number"
                        prefix="CHF"
                        outlined
                        dense
                        dark />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model.number="form.salaryDetails.vorschlagCL"
                        label="Vorschlag Centerleiter (CHF) *"
                        type="number"
                        prefix="CHF"
                        outlined
                        dense
                        dark
                        :rules="[(v) => (v != null && v !== '') || 'Pflichtfeld']" />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model.number="form.salaryDetails.wunschMA"
                        label="Wunsch Mitarbeiter (CHF) *"
                        type="number"
                        prefix="CHF"
                        outlined
                        dense
                        dark
                        :rules="[(v) => (v != null && v !== '') || 'Pflichtfeld']" />
                    </v-col>
                    <v-col cols="12">
                      <v-radio-group
                        v-model="form.salaryDetails.monatszieleErreicht"
                        label="Monatsziele erreicht? *"
                        row
                        dark
                        :rules="[(v) => v !== null || 'Pflichtfeld']">
                        <v-radio label="Ja" :value="true"></v-radio>
                        <v-radio label="Nein" :value="false"></v-radio>
                      </v-radio-group>
                    </v-col>
                    <v-col cols="12" v-if="form.salaryDetails.monatszieleErreicht === false">
                      <v-textarea
                        v-model="form.salaryDetails.monatszieleBegruendung"
                        label="Begründung (warum nicht erreicht?) *"
                        outlined
                        dense
                        dark
                        rows="3"
                        :rules="[(v) => !!v || 'Pflichtfeld bei nicht erreichten Zielen']" />
                    </v-col>
                  </v-row>
                </section>
              </template>
            </v-form>
          </v-card-text>
          <v-divider class="app-divider"></v-divider>
          <v-card-actions class="dialog-actions">
            <span class="dialog-actions__hint d-none d-sm-inline">* Pflichtfelder</span>
            <v-spacer></v-spacer>
            <v-btn text class="secondary-action" @click="closeCreateDialog" :disabled="saving">Abbrechen</v-btn>
            <v-btn
              color="primary"
              depressed
              class="primary-dialog-action"
              :loading="saving"
              :disabled="!formValid || !hasAffectedPerson"
              @click="saveRequest">
              <v-icon left small>{{ isEditMode ? "mdi-content-save-outline" : "mdi-send-outline" }}</v-icon>
              {{ isEditMode ? "Speichern" : "Erfassen & an HR senden" }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- DETAIL DIALOG -->
      <v-dialog :fullscreen="$vuetify.breakpoint.smAndDown" v-model="detailDialog" max-width="980" scrollable content-class="personal-request-dialog">
        <v-card dark v-if="currentRequest" class="app-dialog-card">
          <v-card-title class="dialog-header detail-dialog-header">
            <div class="dialog-header__copy">
              <div class="dialog-header__icon"><v-icon>mdi-file-document-outline</v-icon></div>
              <div class="detail-title-copy">
                <div class="app-eyebrow">{{ currentRequest.category || "Personalanliegen" }}</div>
                <h2>{{ currentRequest.title }}</h2>
                <p>
                  {{ getAffectedPersonName(currentRequest) }}
                  <template v-if="currentRequest.studio && currentRequest.studio.name"> · {{ currentRequest.studio.name }}</template>
                </p>
              </div>
            </div>
            <div class="dialog-header__actions">
              <v-chip small :color="priorityColor(currentRequest.priority)" dark class="status-pill">
                {{ priorityLabel(currentRequest.priority) }}
              </v-chip>
              <v-chip small :color="statusColor(currentRequest.status)" dark class="status-pill">
                {{ statusLabel(currentRequest.status) }}
              </v-chip>
              <v-btn
                icon
                class="touch-action"
                v-if="canEdit(currentRequest)"
                @click="openEditDialog(currentRequest)"
                title="Bearbeiten"
                aria-label="Personalanliegen bearbeiten">
                <v-icon>mdi-pencil-outline</v-icon>
              </v-btn>
              <v-btn icon class="touch-action" title="Dialog schliessen" aria-label="Dialog schliessen" @click="detailDialog = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
          </v-card-title>
          <v-divider class="app-divider"></v-divider>
          <v-card-text class="dialog-scroll-area detail-scroll-area">
            <!-- Visierungs-Banner -->
            <v-alert v-if="canSign && !hasUserSigned(currentRequest)" type="info" color="#009640" text class="signing-banner">
              <div class="signing-banner__content">
                <div>
                  <strong>Deine Visierung ist ausstehend</strong>
                  <span>Prüfe die Angaben und bestätige dieses Anliegen.</span>
                </div>
                <v-btn color="success" depressed class="banner-action" :loading="signing" @click="signRequest">
                  <v-icon left small>mdi-pen</v-icon>Jetzt visieren
                </v-btn>
              </div>
            </v-alert>

            <v-alert v-if="canSign && hasUserSigned(currentRequest)" type="success" text class="signing-banner">
              <div class="signing-banner__content">
                <div>
                  <strong>Anliegen von dir visiert</strong>
                  <span>Deine Bestätigung wurde erfolgreich hinterlegt.</span>
                </div>
                <v-btn text color="error" class="banner-action" :loading="signing" @click="unsignRequest">Visierung zurückziehen</v-btn>
              </div>
            </v-alert>

            <v-row class="detail-grid">
              <v-col cols="12" md="6" class="detail-grid__column">
                <div class="detail-panel">
                  <div class="section-heading section-heading--compact">
                    <div class="section-heading__icon"><v-icon small>mdi-account-details-outline</v-icon></div>
                    <div><h3>Allgemein</h3></div>
                  </div>
                  <detail-row label="Studio" :value="currentRequest.studio && currentRequest.studio.name" />
                  <detail-row label="Kategorie" :value="currentRequest.category" />
                  <detail-row label="Betroffene Person" :value="getAffectedPersonDetail(currentRequest)" />
                  <detail-row
                    label="Personenstatus"
                    :value="currentRequest.isFutureEmployee ? 'Noch kein Mitarbeitender' : 'Bestehender Mitarbeitender'" />
                  <detail-row
                    label="Erstellt von"
                    :value="currentRequest.createdBy ? currentRequest.createdBy.vorname + ' ' + currentRequest.createdBy.nachname : '-'" />
                  <detail-row label="Erstellt am" :value="formatDateTime(currentRequest.createdAt)" />
                </div>
              </v-col>
              <v-col cols="12" md="6" class="detail-grid__column">
                <div class="detail-panel">
                  <div class="section-heading section-heading--compact">
                    <div class="section-heading__icon"><v-icon small>mdi-tune-variant</v-icon></div>
                    <div><h3>Details</h3></div>
                  </div>
                  <detail-row label="Gewünschtes Datum" :value="formatDate(currentRequest.requestedEffectiveDate)" />
                  <detail-row label="HR benachrichtigt" :value="currentRequest.hrMailSent ? 'JA' : 'NEIN'" />
                  <detail-row
                    label="Visierungen"
                    :value="
                      (currentRequest.signatures ? currentRequest.signatures.length : 0) +
                      ' / ' +
                      (currentRequest.requiredSigners ? currentRequest.requiredSigners.length : 0)
                    " />
                  <div class="detail-signature-progress">
                    <v-progress-linear
                      rounded
                      height="6"
                      :value="signatureProgressValue(currentRequest)"
                      :color="currentRequest.fullySigned ? '#55ca82' : '#009640'"
                      background-color="rgba(255, 255, 255, 0.1)" />
                  </div>
                </div>
              </v-col>
              <v-col cols="12" class="detail-grid__column">
                <div class="detail-panel">
                  <div class="section-heading section-heading--compact">
                    <div class="section-heading__icon"><v-icon small>mdi-text-box-outline</v-icon></div>
                    <div><h3>Beschreibung</h3></div>
                  </div>
                  <p class="detail-copy">
                    {{ currentRequest.description || "-" }}
                  </p>
                </div>
              </v-col>
              <v-col
                cols="12"
                class="detail-grid__column"
                v-if="currentRequest.salaryDetails && currentRequest.category === features.salaryCategoryName">
                <div class="detail-panel detail-panel--accent">
                  <div class="section-heading section-heading--compact">
                    <div class="section-heading__icon"><v-icon small>mdi-cash-multiple</v-icon></div>
                    <div><h3>Lohnanfrage – Details</h3></div>
                  </div>
                  <detail-row label="Ausbildung" :value="currentRequest.salaryDetails.ausbildung" />
                  <detail-row label="Branchen-Erfahrung" :value="currentRequest.salaryDetails.branchenErfahrung" />
                  <detail-row label="Dauer bei well come FIT" :value="currentRequest.salaryDetails.dauerBeiWellcomeFit" />
                  <detail-row
                    label="Anfangslohn"
                    :value="
                      currentRequest.salaryDetails.anfangslohn ? 'CHF ' + currentRequest.salaryDetails.anfangslohn.toLocaleString('de-CH') : '-'
                    " />
                  <detail-row
                    label="Letzte Lohnanpassung"
                    :value="
                      currentRequest.salaryDetails.letzteLohnanpassung && currentRequest.salaryDetails.letzteLohnanpassung.datum
                        ? formatDate(currentRequest.salaryDetails.letzteLohnanpassung.datum) +
                          (currentRequest.salaryDetails.letzteLohnanpassung.betrag
                            ? ' (CHF ' + currentRequest.salaryDetails.letzteLohnanpassung.betrag.toLocaleString('de-CH') + ')'
                            : '')
                        : '-'
                    " />
                  <detail-row
                    label="Vorschlag CL"
                    :value="
                      currentRequest.salaryDetails.vorschlagCL ? 'CHF ' + currentRequest.salaryDetails.vorschlagCL.toLocaleString('de-CH') : '-'
                    " />
                  <detail-row
                    label="Wunsch Mitarbeiter"
                    :value="currentRequest.salaryDetails.wunschMA ? 'CHF ' + currentRequest.salaryDetails.wunschMA.toLocaleString('de-CH') : '-'" />
                  <detail-row
                    label="Monatsziele erreicht"
                    :value="
                      currentRequest.salaryDetails.monatszieleErreicht === true
                        ? 'JA'
                        : currentRequest.salaryDetails.monatszieleErreicht === false
                        ? 'NEIN'
                        : '-'
                    " />
                  <detail-row
                    v-if="currentRequest.salaryDetails.monatszieleErreicht === false"
                    label="Begründung"
                    :value="currentRequest.salaryDetails.monatszieleBegruendung" />
                </div>
              </v-col>
              <v-col cols="12" class="detail-grid__column" v-if="currentRequest.creatorComment">
                <div class="detail-panel">
                  <div class="section-heading section-heading--compact">
                    <div class="section-heading__icon"><v-icon small>mdi-comment-quote-outline</v-icon></div>
                    <div><h3>Kommentar Ersteller</h3></div>
                  </div>
                  <p class="detail-copy">
                    {{ currentRequest.creatorComment }}
                  </p>
                </div>
              </v-col>
            </v-row>

            <!-- Visierungen -->
            <section class="detail-panel detail-flow-panel">
              <div class="section-heading section-heading--compact">
                <div class="section-heading__icon"><v-icon small>mdi-check-decagram</v-icon></div>
                <div>
                  <h3>Visierungen</h3>
                  <p>{{ signatureCount(currentRequest) }} von {{ requiredSignerCount(currentRequest) }} abgeschlossen</p>
                </div>
              </div>
              <v-list dense color="transparent" dark class="signer-list">
                <v-list-item v-for="signer in currentRequest.requiredSigners || []" :key="signer._id" class="signer-list__item">
                  <v-list-item-avatar size="36" :color="isSigned(currentRequest, signer._id) ? '#009640' : '#3a3a3c'">
                    <v-icon small dark v-if="isSigned(currentRequest, signer._id)">mdi-check</v-icon>
                    <v-icon small dark v-else>mdi-clock-outline</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{ signer.vorname }} {{ signer.nachname }}</v-list-item-title>
                    <v-list-item-subtitle v-if="isSigned(currentRequest, signer._id)" class="caption">
                      Visiert am {{ getSignatureDate(currentRequest, signer._id) }}
                    </v-list-item-subtitle>
                    <v-list-item-subtitle v-else class="caption">Ausstehend</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item v-if="!currentRequest.requiredSigners || currentRequest.requiredSigners.length === 0">
                  <v-list-item-title class="grey--text">Keine Visierer hinterlegt.</v-list-item-title>
                </v-list-item>
              </v-list>
            </section>

            <!-- Status ändern -->
            <section v-if="canManage" class="detail-panel detail-flow-panel">
              <div class="section-heading section-heading--compact">
                <div class="section-heading__icon"><v-icon small>mdi-progress-wrench</v-icon></div>
                <div>
                  <h3>Status verwalten</h3>
                  <p>Aktuellen Bearbeitungsstand festlegen.</p>
                </div>
              </div>
              <v-btn-toggle v-model="currentRequest.status" mandatory dense class="status-toggle" @change="changeStatus">
                <v-btn value="open"><v-icon left small>mdi-circle-outline</v-icon>Offen</v-btn>
                <v-btn value="in_progress"><v-icon left small>mdi-progress-clock</v-icon>In Bearbeitung</v-btn>
                <v-btn value="done"><v-icon left small>mdi-check-circle-outline</v-icon>Erledigt</v-btn>
                <v-btn value="rejected"><v-icon left small>mdi-close-circle-outline</v-icon>Abgelehnt</v-btn>
              </v-btn-toggle>
            </section>

            <!-- Historie -->
            <section v-if="currentRequest.editHistory && currentRequest.editHistory.length > 0" class="detail-panel detail-flow-panel">
              <div class="section-heading section-heading--compact">
                <div class="section-heading__icon"><v-icon small>mdi-history</v-icon></div>
                <div>
                  <h3>Bearbeitungshistorie</h3>
                  <p>{{ currentRequest.editHistory.length }} {{ currentRequest.editHistory.length === 1 ? "Eintrag" : "Einträge" }}</p>
                </div>
              </div>
              <v-expansion-panels dark flat class="history-panels">
                <v-expansion-panel v-for="(h, idx) in [...currentRequest.editHistory].reverse()" :key="idx">
                  <v-expansion-panel-header>
                    <div class="history-entry__header">
                      <strong>{{ h.user ? h.user.vorname + " " + h.user.nachname : "Unbekannt" }}</strong>
                      <span>{{ formatDateTime(h.editedAt) }}</span>
                      <v-chip small color="#3a3a3c" dark> {{ h.changes ? h.changes.length : 0 }} Änderungen </v-chip>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div v-for="(c, cidx) in h.changes" :key="cidx" class="history-change">
                      <strong>{{ c.field }}</strong>
                      <div>
                        <span class="history-change__old">{{ formatHistoryValue(c.oldValue, c.field) }}</span>
                        <v-icon small>mdi-arrow-right</v-icon>
                        <span class="history-change__new">{{ formatHistoryValue(c.newValue, c.field) }}</span>
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </section>

            <!-- Kommentare -->
            <section class="detail-panel detail-flow-panel comments-panel">
              <div class="section-heading section-heading--compact">
                <div class="section-heading__icon"><v-icon small>mdi-comment-multiple-outline</v-icon></div>
                <div>
                  <h3>Kommentare</h3>
                  <p>{{ currentRequest.comments ? currentRequest.comments.length : 0 }} Beiträge</p>
                </div>
              </div>
              <div v-if="currentRequest.comments && currentRequest.comments.length > 0" class="comment-list">
                <v-card v-for="(c, idx) in currentRequest.comments" :key="idx" class="comment-card" flat>
                  <div class="comment-card__header">
                    <strong>{{ c.user ? c.user.vorname + " " + c.user.nachname : "Unbekannt" }}</strong>
                    <span>{{ formatDateTime(c.createdAt) }}</span>
                  </div>
                  <div class="comment-card__copy">{{ c.comment }}</div>
                </v-card>
              </div>
              <p v-else class="empty-comments">Noch keine Kommentare.</p>

              <v-textarea v-model="newComment" label="Kommentar hinzufügen" outlined dense dark rows="2" class="comment-input" hide-details />
              <div class="comment-actions">
                <v-btn color="primary" depressed :disabled="!newComment.trim()" :loading="savingComment" @click="addComment">
                  <v-icon left small>mdi-send</v-icon>Kommentar senden
                </v-btn>
              </div>
            </section>
          </v-card-text>
          <v-divider class="app-divider"></v-divider>
          <v-card-actions class="dialog-actions detail-dialog-actions">
            <v-btn text color="error" class="danger-action" v-if="canManage" @click="deleteRequest">
              <v-icon left>mdi-delete-outline</v-icon>Löschen
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" outlined class="secondary-action" @click="downloadPdf(currentRequest)">
              <v-icon left>mdi-file-pdf-box</v-icon>PDF
            </v-btn>
            <v-btn text class="secondary-action" @click="detailDialog = false">Schliessen</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import userSearch from "@/components/userSearch.vue"
import { mapState, mapGetters } from "vuex"

const DetailRow = {
  props: ["label", "value"],
  render(h) {
    return h("div", { class: "detail-row" }, [
      h("span", { class: "detail-row__label" }, this.label),
      h("span", { class: "detail-row__value" }, this.value === 0 ? "0" : this.value || "-"),
    ])
  },
}

export default {
  name: "PersonalRequests",
  components: {
    userSearch,
    "detail-row": DetailRow,
  },
  data() {
    return {
      userService,
      loading: false,
      saving: false,
      savingComment: false,
      signing: false,
      loadingCategories: false,
      requests: [],
      categoryOptions: [],
      categorySearch: "",
      filterStudio: "all",
      filterStatus: "open",
      filterPriority: "all",
      editingId: null,
      createDialog: false,
      detailDialog: false,
      formValid: false,
      currentRequest: null,
      newComment: "",
      features: {
        salaryRequestsEnabled: false,
        salaryCategoryName: "Lohnanpassung",
      },
      form: this.getEmptyForm(),
      tableHeaders: [
        { text: "Erstellt", value: "createdAt" },
        { text: "Titel", value: "title" },
        { text: "Kategorie", value: "category" },
        { text: "Betroffene Person", value: "affectedEmployee", sortable: false },
        { text: "Studio", value: "studio" },
        { text: "Priorität", value: "priority" },
        { text: "Status", value: "status" },
        { text: "Visiert", value: "signatures", sortable: false },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
      statusFilterOptions: [
        { text: "Alle", value: "all" },
        { text: "Offen", value: "open" },
        { text: "In Bearbeitung", value: "in_progress" },
        { text: "Erledigt", value: "done" },
        { text: "Abgelehnt", value: "rejected" },
      ],
      priorityFilterOptions: [
        { text: "Alle", value: "all" },
        { text: "Hoch", value: "high" },
        { text: "Normal", value: "normal" },
        { text: "Tief", value: "low" },
      ],
      priorityOptions: [
        { text: "Tief", value: "low" },
        { text: "Normal", value: "normal" },
        { text: "Hoch", value: "high" },
      ],
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    ...mapGetters("studioStore", ["getSelectedStudioId"]),
    isSalaryRequest() {
      const cat = typeof this.form.category === "object" ? this.form.category?.text : this.form.category
      return (cat || "").trim().toLowerCase() === (this.features.salaryCategoryName || "").toLowerCase()
    },
    isEditMode() {
      return !!this.editingId
    },
    hasAffectedPerson() {
      if (this.form.isFutureEmployee) return !!String(this.form.affectedPersonName || "").trim()
      return !!this.form.affectedEmployee
    },
    canManage() {
      return this.userService.hasRight("d_managePersonalRequests")
    },
    canSign() {
      // User darf visieren, wenn er in der requiredSigners-Liste des aktuellen Requests ist
      if (!this.currentRequest || !this.currentRequest.requiredSigners) return false
      const uid = this.userService.getUserId().toString()
      return this.currentRequest.requiredSigners.some((s) => s._id.toString() === uid)
    },
    studioOptions() {
      return this.storeStudios.filter((s) => s.code && s.code !== "ot" && s.code !== "alibey").map((s) => ({ _id: s._id, name: s.name }))
    },
    studioFilterOptions() {
      return [{ _id: "all", name: "Alle Studios" }, ...this.studioOptions]
    },
    highPriorityCount() {
      return this.requests.filter((request) => request.priority === "high").length
    },
    fullySignedCount() {
      return this.requests.filter((request) => request.fullySigned).length
    },
    hasActiveFilters() {
      return (this.canManage && this.filterStudio !== "all") || this.filterStatus !== "open" || this.filterPriority !== "all"
    },
  },
  watch: {
    categorySearch(val) {
      if (val && val.length >= 2) {
        this.searchCategories(val)
      }
    },
  },
  methods: {
    resetFilters() {
      this.filterStudio = "all"
      this.filterStatus = "open"
      this.filterPriority = "all"
      this.loadRequests()
    },
    signatureCount(request) {
      return request && Array.isArray(request.signatures) ? request.signatures.length : 0
    },
    requiredSignerCount(request) {
      return request && Array.isArray(request.requiredSigners) ? request.requiredSigners.length : 0
    },
    signatureProgressValue(request) {
      const required = this.requiredSignerCount(request)
      if (!required) return 0
      return Math.min(100, (this.signatureCount(request) / required) * 100)
    },
    formatBirthday(user) {
      if (!user) return "Geburtsdatum unbekannt"
      const birthday = user.birthday || user.birthDate || user.dateOfBirth || user.geburtstag || user.geburtsdatum
      if (!birthday) return "Geburtsdatum unbekannt"
      const value = this.$moment(birthday)
      return value.isValid() ? value.format("DD.MM.YYYY") : String(birthday)
    },
    getAffectedPersonName(request) {
      if (request && request.affectedEmployee) {
        const employee = request.affectedEmployee
        const name = `${employee.vorname || ""} ${employee.nachname || ""}`.trim()
        if (name) return name
      }
      return String((request && request.affectedPersonName) || "").trim() || "-"
    },
    getAffectedPersonDetail(request) {
      const name = this.getAffectedPersonName(request)
      if (!request || request.isFutureEmployee || !request.affectedEmployee) return name
      return `${name} (${this.formatBirthday(request.affectedEmployee)})`
    },
    getEmptyForm() {
      return {
        studio: this.canManage ? null : this.getSelectedStudioId,
        affectedEmployee: null,
        isFutureEmployee: false,
        affectedPersonName: "",
        category: "",
        title: "",
        description: "",
        creatorComment: "",
        requestedEffectiveDate: null,
        priority: "normal",
        salaryDetails: {
          ausbildung: "",
          branchenErfahrung: "",
          dauerBeiWellcomeFit: "",
          anfangslohn: null,
          letzteLohnanpassung: { datum: null, betrag: null },
          vorschlagCL: null,
          wunschMA: null,
          monatszieleErreicht: null,
          monatszieleBegruendung: "",
        },
      }
    },
    async loadRequests() {
      this.loading = true
      try {
        const params = {}
        if (this.canManage && this.filterStudio && this.filterStudio !== "all") params.studio = this.filterStudio
        if (this.filterStatus && this.filterStatus !== "all") params.status = this.filterStatus
        if (this.filterPriority && this.filterPriority !== "all") params.priority = this.filterPriority
        this.requests = await api.personalRequests.fetch(params)
      } catch (e) {
        console.error("Fehler beim Laden:", e)
      } finally {
        this.loading = false
      }
    },
    async loadCategories() {
      this.loadingCategories = true
      try {
        const cats = await api.personalRequests.fetchCategories()
        this.categoryOptions = cats.map((c) => c.name)
      } catch (e) {
        console.error("Fehler beim Laden der Kategorien:", e)
      } finally {
        this.loadingCategories = false
      }
    },
    async searchCategories(query) {
      try {
        const cats = await api.personalRequests.fetchCategories(query)
        const names = cats.map((c) => c.name)
        // Mergen mit bereits geladenen Kategorien
        const merged = [...new Set([...this.categoryOptions, ...names])]
        this.categoryOptions = merged
      } catch (e) {
        // silent
      }
    },
    openCreateDialog() {
      this.form = this.getEmptyForm()
      this.editingId = null
      this.createDialog = true
      this.$nextTick(() => {
        if (this.$refs.createForm) this.$refs.createForm.resetValidation()
      })
    },
    openEditDialog(request) {
      const emptyForm = this.getEmptyForm()
      const isFutureEmployee = request.isFutureEmployee === true || (!request.affectedEmployee && !!String(request.affectedPersonName || "").trim())
      this.form = {
        studio: request.studio && request.studio._id ? request.studio._id : request.studio,
        affectedEmployee: request.affectedEmployee && request.affectedEmployee._id ? request.affectedEmployee._id : request.affectedEmployee,
        isFutureEmployee,
        affectedPersonName: request.affectedPersonName || "",
        category: request.category || "",
        title: request.title || "",
        description: request.description || "",
        creatorComment: request.creatorComment || "",
        requestedEffectiveDate: request.requestedEffectiveDate ? this.$moment(request.requestedEffectiveDate).format("YYYY-MM-DD") : null,
        priority: request.priority || "normal",
        salaryDetails: request.salaryDetails || emptyForm.salaryDetails,
      }
      this.editingId = request._id
      this.detailDialog = false
      this.createDialog = true
    },
    closeCreateDialog() {
      this.createDialog = false
      this.editingId = null
    },
    updateAffectedEmployee(value) {
      // userSearch gibt entweder die User-ID oder ein User-Objekt zurück
      if (!value) {
        this.form.affectedEmployee = null
      } else if (typeof value === "object") {
        this.form.affectedEmployee = value._id || value
      } else {
        this.form.affectedEmployee = value
      }
    },
    onFutureEmployeeChange(isFutureEmployee) {
      if (isFutureEmployee) {
        this.form.affectedEmployee = null
      } else {
        this.form.affectedPersonName = ""
      }
      this.$nextTick(() => {
        if (this.$refs.createForm) this.$refs.createForm.validate()
      })
    },
    async saveRequest() {
      if (!this.$refs.createForm.validate()) return
      if (!this.hasAffectedPerson) {
        this.$toast && this.$toast.error("Bitte die betroffene Person angeben")
        return
      }
      this.saving = true
      try {
        // Kategorie kann vom v-combobox ein Objekt oder String sein
        const payload = { ...this.form }
        if (typeof payload.category === "object" && payload.category !== null) {
          payload.category = payload.category.text || payload.category.name || ""
        }
        payload.isFutureEmployee = payload.isFutureEmployee === true
        payload.affectedEmployee = payload.isFutureEmployee ? null : payload.affectedEmployee
        payload.affectedPersonName = payload.isFutureEmployee ? String(payload.affectedPersonName || "").trim() : ""

        if (this.isEditMode) {
          const updated = await api.personalRequests.update(this.editingId, payload)
          this.currentRequest = updated
        } else {
          await api.personalRequests.create(payload)
        }
        this.createDialog = false
        this.editingId = null
        this.loadRequests()
        this.loadCategories()
      } catch (e) {
        console.error("Fehler beim Speichern:", e)
        const msg = e.response && e.response.data && e.response.data.error ? e.response.data.error : "Fehler beim Speichern"
        alert(msg)
      } finally {
        this.saving = false
      }
    },
    async openDetailDialog(request) {
      try {
        this.currentRequest = await api.personalRequests.fetchOne(request._id)
        this.newComment = ""
        this.detailDialog = true
      } catch (e) {
        console.error("Fehler beim Laden des Details:", e)
      }
    },
    async changeStatus(status) {
      if (!this.currentRequest) return
      try {
        const updated = await api.personalRequests.updateStatus(this.currentRequest._id, status)
        this.currentRequest = updated
        this.loadRequests()
      } catch (e) {
        console.error("Fehler beim Statuswechsel:", e)
      }
    },
    async signRequest() {
      if (!this.currentRequest) return
      this.signing = true
      try {
        const updated = await api.personalRequests.sign(this.currentRequest._id)
        this.currentRequest = updated
        this.loadRequests()
      } catch (e) {
        console.error("Fehler beim Visieren:", e)
        alert(e.response && e.response.data && e.response.data.error ? e.response.data.error : "Fehler beim Visieren")
      } finally {
        this.signing = false
      }
    },
    async unsignRequest() {
      if (!this.currentRequest) return
      if (!confirm("Visierung wirklich zurückziehen?")) return
      this.signing = true
      try {
        const updated = await api.personalRequests.unsign(this.currentRequest._id)
        this.currentRequest = updated
        this.loadRequests()
      } catch (e) {
        console.error("Fehler beim Zurückziehen:", e)
      } finally {
        this.signing = false
      }
    },
    async addComment() {
      if (!this.newComment.trim() || !this.currentRequest) return
      this.savingComment = true
      try {
        const updated = await api.personalRequests.addComment(this.currentRequest._id, this.newComment)
        this.currentRequest = updated
        this.newComment = ""
        this.loadRequests()
      } catch (e) {
        console.error("Fehler beim Kommentar:", e)
      } finally {
        this.savingComment = false
      }
    },
    async deleteRequest() {
      if (!this.currentRequest) return
      if (!confirm("Personalanliegen wirklich löschen?")) return
      try {
        await api.personalRequests.delete(this.currentRequest._id)
        this.detailDialog = false
        this.loadRequests()
      } catch (e) {
        console.error("Fehler beim Löschen:", e)
      }
    },
    downloadPdf(request) {
      const personName = this.getAffectedPersonName(request)
        .replace(/[\\/:*?"<>|]+/g, "-")
        .replace(/\s+/g, "_")
      const fileName = `Personalanliegen_${personName}_${this.$moment(request.createdAt).format("YYYY-MM-DD")}.pdf`
      api.personalRequests.downloadPdf(request._id, fileName)
    },
    canEdit(request) {
      if (!request || request.status === "done") return false
      if (this.canManage) return true
      const creatorId = request.createdBy && request.createdBy._id ? request.createdBy._id : request.createdBy
      return creatorId && creatorId.toString() === this.userService.getUserId().toString()
    },
    hasUserSigned(request) {
      if (!request || !request.signatures) return false
      const uid = this.userService.getUserId().toString()
      return request.signatures.some((s) => {
        const sid = s.user && s.user._id ? s.user._id.toString() : s.user.toString()
        return sid === uid
      })
    },
    isSigned(request, userId) {
      if (!request || !request.signatures) return false
      return request.signatures.some((s) => {
        const sid = s.user && s.user._id ? s.user._id.toString() : s.user.toString()
        return sid === userId.toString()
      })
    },
    getSignatureDate(request, userId) {
      if (!request || !request.signatures) return ""
      const sig = request.signatures.find((s) => {
        const sid = s.user && s.user._id ? s.user._id.toString() : s.user.toString()
        return sid === userId.toString()
      })
      return sig ? this.$moment(sig.signedAt).format("DD.MM.YYYY HH:mm") : ""
    },
    formatDate(d) {
      return d ? this.$moment(d).format("DD.MM.YYYY") : "-"
    },
    formatDateTime(d) {
      return d ? this.$moment(d).format("DD.MM.YYYY HH:mm") : "-"
    },
    formatHistoryValue(val, fieldLabel) {
      if (val === null || val === undefined || val === "") return "-"
      if (typeof val === "boolean") return val ? "JA" : "NEIN"
      if (typeof val === "object" && val !== null) {
        if (val.vorname) return `${val.vorname} ${val.nachname || ""}`.trim()
        if (val.name) return val.name
        if (val._id) return val._id
        return JSON.stringify(val)
      }
      if (typeof val === "string" && val.match(/^\d{4}-\d{2}-\d{2}/)) {
        const m = this.$moment(val)
        if (m.isValid()) {
          return fieldLabel === "Gewünschtes Datum" ? m.format("DD.MM.YYYY") : m.format("DD.MM.YYYY")
        }
      }
      return String(val)
    },
    statusColor(s) {
      if (s === "done") return "green"
      if (s === "in_progress") return "orange"
      if (s === "rejected") return "red darken-2"
      return "red"
    },
    statusLabel(s) {
      if (s === "done") return "Erledigt"
      if (s === "in_progress") return "In Bearbeitung"
      if (s === "rejected") return "Abgelehnt"
      return "Offen"
    },
    priorityColor(p) {
      if (p === "high") return "red"
      if (p === "low") return "grey"
      return "#4b4b4f"
    },
    priorityLabel(p) {
      if (p === "high") return "Hoch"
      if (p === "low") return "Tief"
      return "Normal"
    },
  },
  async created() {
    try {
      this.features = await api.personalRequests.fetchFeatures()
    } catch (e) {
      console.error("Feature-Flags konnten nicht geladen werden:", e)
    }
    this.loadRequests()
    this.loadCategories()
  },
}
</script>

<style scoped>
.personal-requests-page {
  --pr-primary: #009640;
  --pr-primary-hover: #00a848;
  --pr-primary-soft: rgba(0, 150, 64, 0.14);
  --pr-surface: rgba(0, 0, 0, 0.2);
  --pr-surface-raised: rgba(255, 255, 255, 0.045);
  --pr-surface-soft: rgba(255, 255, 255, 0.028);
  --pr-border: rgba(255, 255, 255, 0.1);
  --pr-border-strong: rgba(255, 255, 255, 0.14);
  --pr-text: #f4f4f5;
  --pr-muted: #9b9ba0;
  min-height: 100%;
  padding: clamp(16px, 2.5vw, 36px);
  color: var(--pr-text);
  background: transparent;
}

.personal-requests-shell {
  width: 100%;
  max-width: 1480px;
  margin: 0 auto;
}

.page-hero {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 28px;
  min-height: 154px;
  padding: clamp(24px, 3vw, 40px);
  overflow: hidden;
  border: 1px solid var(--pr-border-strong);
  border-radius: 24px;
  background: radial-gradient(circle at 92% 8%, rgba(0, 150, 64, 0.1), transparent 34%),
    linear-gradient(110deg, rgba(0, 0, 0, 0.3), rgba(255, 255, 255, 0.025)), var(--pr-surface);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.24);
  backdrop-filter: blur(18px);
}

.page-hero::after {
  position: absolute;
  top: -120px;
  right: -60px;
  width: 340px;
  height: 340px;
  border-radius: 50%;
  border: 1px solid rgba(0, 150, 64, 0.12);
  background: transparent;
  content: "";
  pointer-events: none;
}

.page-hero__copy,
.dialog-header__copy {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 18px;
}

.page-hero__icon,
.dialog-header__icon,
.empty-state__icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  border: 1px solid rgba(0, 150, 64, 0.34);
  border-radius: 18px;
  color: #d8f3e2;
  background: rgba(0, 150, 64, 0.16);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 10px 24px rgba(0, 0, 0, 0.16);
}

.page-hero__icon .v-icon {
  color: #d8f3e2 !important;
  font-size: 30px;
}

.app-eyebrow {
  margin-bottom: 5px;
  color: #65ca8b;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.13em;
  line-height: 1.2;
  text-transform: uppercase;
}

.page-hero h1,
.dialog-header h2 {
  margin: 0;
  color: #ffffff;
  font-size: clamp(27px, 3vw, 38px);
  font-weight: 750;
  letter-spacing: -0.035em;
  line-height: 1.12;
}

.page-hero p,
.dialog-header p {
  max-width: 680px;
  margin: 8px 0 0;
  color: #a9a9ae;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.55;
}

.hero-cta,
.primary-dialog-action,
.banner-action,
.card-action,
.secondary-action,
.danger-action,
.reset-button {
  letter-spacing: 0 !important;
  text-transform: none !important;
}

.hero-cta {
  z-index: 1;
  flex: 0 0 auto;
  min-height: 48px;
  padding: 0 22px !important;
  border-radius: 14px;
  color: #ffffff !important;
  background: var(--pr-primary) !important;
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.22);
  font-weight: 700;
}

.hero-cta:hover,
.hero-cta:focus-visible {
  background: var(--pr-primary-hover) !important;
}

.summary-grid {
  margin-top: 16px;
}

.summary-card {
  display: flex;
  align-items: center;
  min-height: 112px;
  height: 100%;
  gap: 16px;
  padding: 20px;
  border: 1px solid var(--pr-border);
  border-radius: 20px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.045), rgba(0, 0, 0, 0.18));
  box-shadow: 0 14px 38px rgba(0, 0, 0, 0.16);
  backdrop-filter: blur(16px);
  transition: transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease;
}

.summary-card:hover {
  transform: translateY(-2px);
  border-color: rgba(255, 255, 255, 0.16);
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.22);
}

.summary-card__icon {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 15px;
}

.summary-card__icon--primary {
  color: #ccebd8;
  background: rgba(0, 150, 64, 0.16);
}

.summary-card__icon--priority {
  color: #f2c56d;
  background: rgba(230, 164, 35, 0.13);
}

.summary-card__icon--success {
  color: #72d697;
  background: rgba(0, 150, 64, 0.13);
}

.summary-card__icon .v-icon {
  color: inherit;
}

.summary-card > div:last-child {
  display: grid;
  min-width: 0;
  grid-template-columns: auto 1fr;
  align-items: baseline;
  column-gap: 8px;
}

.summary-card__label {
  grid-column: 1 / -1;
  margin-bottom: 4px;
  color: var(--pr-muted);
  font-size: 12px;
  font-weight: 650;
}

.summary-card strong {
  color: #ffffff;
  font-size: 28px;
  letter-spacing: -0.035em;
  line-height: 1;
}

.summary-card small {
  overflow: hidden;
  color: #aaaaaf;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-surface,
.requests-surface {
  margin-top: 16px;
  border: 1px solid var(--pr-border);
  border-radius: 22px;
  background: rgba(0, 0, 0, 0.18);
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.18);
  backdrop-filter: blur(18px);
}

.filter-surface {
  padding: 22px;
}

.surface-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
}

.surface-heading h2 {
  margin: 0;
  color: var(--pr-text);
  font-size: 18px;
  font-weight: 720;
  letter-spacing: -0.015em;
}

.surface-heading__meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.result-count {
  padding: 7px 11px;
  border: 1px solid rgba(0, 150, 64, 0.24);
  border-radius: 999px;
  color: #ccebd8;
  background: rgba(0, 150, 64, 0.11);
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.reset-button {
  min-height: 44px;
  border-radius: 12px;
  color: #c7c7cb !important;
}

.filter-grid {
  margin-bottom: -4px;
}

.filter-surface ::v-deep .v-input__slot,
.app-dialog-card ::v-deep .v-input__slot {
  border-radius: 13px !important;
  background: rgba(255, 255, 255, 0.035) !important;
}

.filter-surface ::v-deep .v-text-field--outlined fieldset,
.app-dialog-card ::v-deep .v-text-field--outlined fieldset {
  border-color: rgba(255, 255, 255, 0.14);
  transition: border-color 160ms ease;
}

.filter-surface ::v-deep .v-input--is-focused fieldset,
.app-dialog-card ::v-deep .v-input--is-focused fieldset {
  border-width: 1px;
  border-color: var(--pr-primary) !important;
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.12);
}

.filter-surface ::v-deep .v-label,
.app-dialog-card ::v-deep .v-label {
  color: #9d9da2;
}

.requests-surface {
  overflow-x: auto;
}

.requests-table {
  min-width: 1120px;
  overflow: hidden;
  border-radius: 22px;
  background: transparent !important;
}

.requests-table ::v-deep .v-data-table__wrapper,
.requests-table ::v-deep table {
  background: transparent;
}

.requests-table ::v-deep thead th {
  height: 52px;
  border-bottom: 1px solid var(--pr-border) !important;
  color: #949499 !important;
  background: rgba(0, 0, 0, 0.2);
  font-size: 11px;
  font-weight: 750;
  letter-spacing: 0.075em;
  text-transform: uppercase;
  white-space: nowrap;
}

.requests-table ::v-deep tbody tr {
  cursor: pointer;
  transition: background-color 160ms ease, box-shadow 160ms ease;
}

.requests-table ::v-deep tbody tr:hover {
  background: rgba(255, 255, 255, 0.045) !important;
  box-shadow: inset 3px 0 var(--pr-primary);
}

.requests-table ::v-deep tbody td {
  height: 70px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.075) !important;
  color: #d4d4d7;
  font-size: 13px;
}

.requests-table ::v-deep .v-data-footer,
.mobile-iterator ::v-deep .v-data-footer {
  min-height: 60px;
  border-top: 1px solid var(--pr-border);
  color: #99999e;
  background: rgba(0, 0, 0, 0.16);
}

.table-date {
  color: #a2a2a7;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.table-title-cell {
  max-width: 230px;
}

.table-title-cell strong {
  display: block;
  overflow: hidden;
  color: var(--pr-text);
  font-size: 14px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table-person {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  max-width: 190px;
  gap: 6px;
}

.table-person .v-icon {
  color: #838388;
}

.future-person-badge {
  display: inline-flex;
  align-items: center;
  min-height: 1.5rem;
  padding: 0.125rem 0.5rem;
  border: 1px solid rgba(85, 202, 130, 0.24);
  border-radius: 999px;
  color: #8de0ad;
  background: rgba(0, 150, 64, 0.12);
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.25;
  white-space: nowrap;
}

.status-pill {
  height: 27px !important;
  padding: 0 10px !important;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px !important;
  box-shadow: none;
  font-size: 11px !important;
  font-weight: 750;
  letter-spacing: 0.01em;
}

.status-pill__dot {
  width: 6px;
  height: 6px;
  margin-right: 7px;
  border-radius: 50%;
  background: currentColor;
}

.signature-cell {
  width: 84px;
}

.signature-cell__label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 7px;
  color: #c6c6ca;
  font-size: 11px;
  font-weight: 700;
}

.table-actions {
  display: flex;
  justify-content: flex-end;
  gap: 2px;
}

.touch-action {
  width: 44px !important;
  min-width: 44px !important;
  height: 44px !important;
  border-radius: 13px;
  color: #b8b8bd !important;
}

.touch-action:hover {
  color: #ffffff !important;
  background: rgba(0, 150, 64, 0.13);
}

.mobile-requests {
  margin-top: 16px;
}

.mobile-iterator {
  background: transparent !important;
}

.mobile-iterator ::v-deep .v-data-iterator__actions {
  margin-top: 12px;
}

.mobile-request-grid {
  display: grid;
  gap: 14px;
}

.request-card {
  overflow: hidden;
  border: 1px solid var(--pr-border);
  border-radius: 20px;
  outline: none;
  color: var(--pr-text);
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.045), rgba(0, 0, 0, 0.2));
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(16px);
  transition: transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease;
}

.request-card:hover,
.request-card:focus-visible {
  transform: translateY(-2px);
  border-color: rgba(0, 150, 64, 0.42);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.26), 0 0 0 3px rgba(0, 150, 64, 0.11);
}

.request-card__top,
.request-card__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.request-card__top {
  padding: 15px 16px 0;
}

.request-card__pills {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}

.request-card__body {
  padding: 16px;
}

.request-card__category {
  display: block;
  margin-bottom: 5px;
  color: #65ca8b;
  font-size: 11px;
  font-weight: 750;
  letter-spacing: 0.055em;
  text-transform: uppercase;
}

.request-card h3 {
  margin: 0;
  color: #ffffff;
  font-size: 18px;
  font-weight: 720;
  letter-spacing: -0.02em;
  line-height: 1.35;
}

.request-card__meta {
  display: grid;
  gap: 9px;
  margin-top: 14px;
}

.request-card__meta span {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 8px;
  color: #b7b7bc;
  font-size: 13px;
}

.request-card__meta .v-icon {
  color: #85858a;
}

.request-card__signatures {
  margin-top: 17px;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 13px;
  background: rgba(0, 0, 0, 0.16);
}

.request-card__signatures > div:first-child {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  color: #9b9ba0;
  font-size: 11px;
}

.request-card__signatures strong {
  color: #e5e5e7;
}

.request-card__actions {
  padding: 8px 10px;
  border-top: 1px solid var(--pr-border);
  background: rgba(0, 0, 0, 0.12);
}

.card-action {
  min-height: 44px;
  border-radius: 12px;
  font-weight: 700;
}

.card-action.primary--text {
  color: #65ca8b !important;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 20px;
  border: 1px dashed rgba(255, 255, 255, 0.14);
  border-radius: 20px;
  text-align: center;
  background: rgba(0, 0, 0, 0.14);
}

.empty-state__icon {
  width: 52px;
  height: 52px;
  margin-bottom: 14px;
}

.empty-state h3 {
  margin: 0;
  color: var(--pr-text);
  font-size: 17px;
}

.empty-state p {
  margin: 6px 0 0;
  color: var(--pr-muted);
  font-size: 13px;
}

.app-dialog-card {
  --pr-primary: #009640;
  --pr-primary-hover: #00a848;
  --pr-text: #f4f4f5;
  --pr-muted: #9b9ba0;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  border-radius: 24px !important;
  color: var(--pr-text);
  background: radial-gradient(circle at 90% -10%, rgba(0, 150, 64, 0.09), transparent 24rem), #202021 !important;
  box-shadow: 0 32px 90px rgba(0, 0, 0, 0.58) !important;
}

.dialog-header {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  justify-content: space-between;
  min-height: 108px;
  gap: 20px;
  padding: 24px 26px !important;
  background: rgba(0, 0, 0, 0.22);
}

.dialog-header__icon {
  width: 46px;
  height: 46px;
  border-radius: 15px;
}

.dialog-header__icon .v-icon {
  color: #d8f3e2;
}

.dialog-header h2 {
  overflow-wrap: anywhere;
  font-size: 23px;
}

.dialog-header p {
  margin-top: 5px;
  font-size: 13px;
  white-space: normal;
}

.dialog-close {
  flex: 0 0 auto;
}

.dialog-header__actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  gap: 7px;
}

.detail-title-copy {
  min-width: 0;
}

.app-divider {
  border-color: rgba(255, 255, 255, 0.09) !important;
}

.dialog-scroll-area {
  max-height: min(72vh, 780px);
  padding: 24px 26px 28px !important;
}

.form-stack {
  display: grid;
  gap: 16px;
}

.form-section,
.detail-panel {
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.032);
}

.form-section--accent,
.detail-panel--accent {
  border-color: rgba(0, 150, 64, 0.24);
  background: linear-gradient(145deg, rgba(0, 150, 64, 0.09), rgba(255, 255, 255, 0.025));
}

.person-mode-card {
  padding: 0.875rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.875rem;
  background: rgba(0, 0, 0, 0.14);
}

.person-mode-switch {
  margin: 0 !important;
  padding: 0 !important;
}

.person-mode-switch ::v-deep .v-label {
  color: var(--pr-text);
  font-size: 1rem;
  line-height: 1.4;
}

.person-mode-card p {
  margin: 0.375rem 0 0 3.25rem;
  color: var(--pr-muted);
  font-size: 0.875rem;
  line-height: 1.5;
}

.section-heading {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  margin-bottom: 18px;
}

.section-heading__icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 11px;
  color: #d8f3e2;
  background: rgba(0, 150, 64, 0.14);
}

.section-heading__icon .v-icon {
  color: inherit;
}

.section-heading h3 {
  margin: 1px 0 0;
  color: var(--pr-text);
  font-size: 15px;
  font-weight: 730;
  letter-spacing: -0.01em;
  line-height: 1.3;
}

.section-heading p {
  margin: 3px 0 0;
  color: #919196;
  font-size: 12px;
  line-height: 1.4;
}

.section-heading--compact {
  margin-bottom: 14px;
}

.app-dialog-card ::v-deep .v-subheader {
  height: auto;
  min-height: 28px;
  color: #c8c8cc;
  font-size: 13px;
  font-weight: 650;
}

.dialog-actions {
  flex: 0 0 auto;
  min-height: 74px;
  padding: 13px 20px !important;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  background: rgba(18, 18, 19, 0.92);
  backdrop-filter: blur(16px);
}

.dialog-actions__hint {
  color: #85858a;
  font-size: 12px;
}

.primary-dialog-action {
  min-height: 44px;
  padding: 0 18px !important;
  border-radius: 13px;
  font-weight: 750;
  color: #ffffff !important;
  background: var(--pr-primary) !important;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.22);
}

.primary-dialog-action:hover,
.primary-dialog-action:focus-visible {
  background: var(--pr-primary-hover) !important;
}

.primary-dialog-action.v-btn--disabled,
.comment-actions .v-btn.v-btn--disabled {
  color: rgba(255, 255, 255, 0.34) !important;
  background: rgba(255, 255, 255, 0.075) !important;
  box-shadow: none;
}

.secondary-action,
.danger-action {
  min-height: 44px;
  border-radius: 12px;
  font-weight: 650;
}

.secondary-action.primary--text {
  border-color: rgba(0, 150, 64, 0.4) !important;
  color: #65ca8b !important;
}

.signing-banner {
  margin: 0 0 16px !important;
  border: 1px solid currentColor;
  border-radius: 16px;
  background: rgba(0, 150, 64, 0.07) !important;
}

.signing-banner__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 18px;
}

.signing-banner__content > div {
  display: grid;
  gap: 2px;
}

.signing-banner__content strong {
  color: var(--pr-text);
  font-size: 14px;
}

.signing-banner__content span {
  color: #b9b9bd;
  font-size: 12px;
}

.banner-action {
  flex: 0 0 auto;
  min-height: 44px;
  border-radius: 12px;
  font-weight: 700;
}

.detail-grid {
  margin: -6px !important;
}

.detail-grid__column {
  display: flex;
  padding: 6px !important;
}

.detail-grid__column > .detail-panel {
  width: 100%;
}

.detail-panel {
  min-width: 0;
}

.detail-flow-panel {
  margin-top: 12px;
}

.detail-copy {
  margin: 0;
  color: #cdcdcf;
  font-size: 14px;
  line-height: 1.7;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.detail-row {
  display: grid;
  grid-template-columns: minmax(112px, 0.85fr) minmax(0, 1.25fr);
  align-items: start;
  gap: 14px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  font-size: 13px;
  line-height: 1.45;
}

.detail-row:last-child {
  border-bottom: 0;
}

.detail-row ::v-deep .detail-row__label {
  color: #8e8e93;
}

.detail-row ::v-deep .detail-row__value {
  min-width: 0;
  color: #dddddf;
  font-weight: 550;
  overflow-wrap: anywhere;
}

.detail-signature-progress {
  margin-top: 12px;
}

.signer-list {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 14px;
  background: rgba(0, 0, 0, 0.14) !important;
}

.signer-list__item {
  min-height: 60px;
  padding: 4px 14px !important;
}

.signer-list__item + .signer-list__item {
  border-top: 1px solid rgba(255, 255, 255, 0.07);
}

.signer-list ::v-deep .v-list-item__title {
  color: #e2e2e4;
  font-size: 13px;
  font-weight: 650;
}

.signer-list ::v-deep .v-list-item__subtitle {
  color: #929297 !important;
}

.status-toggle {
  display: flex;
  width: 100%;
  overflow: visible;
  gap: 7px;
  border-radius: 14px;
  background: transparent !important;
}

.status-toggle ::v-deep .v-btn {
  flex: 1 1 auto;
  min-width: 0 !important;
  min-height: 44px;
  padding: 0 12px !important;
  border: 1px solid rgba(255, 255, 255, 0.11) !important;
  border-radius: 12px !important;
  color: #adadb2;
  background: rgba(0, 0, 0, 0.16) !important;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.status-toggle ::v-deep .v-btn--active {
  border-color: rgba(0, 150, 64, 0.48) !important;
  color: #ffffff !important;
  background: rgba(0, 150, 64, 0.2) !important;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.16);
}

.history-panels {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 14px;
}

.history-panels ::v-deep .v-expansion-panel {
  color: #dddddf;
  background: rgba(0, 0, 0, 0.15) !important;
}

.history-panels ::v-deep .v-expansion-panel::before {
  box-shadow: none;
}

.history-panels ::v-deep .v-expansion-panel + .v-expansion-panel {
  border-top: 1px solid rgba(255, 255, 255, 0.07);
}

.history-entry__header {
  display: flex;
  align-items: center;
  min-width: 0;
  flex-wrap: wrap;
  gap: 7px;
}

.history-entry__header > span {
  color: #929297;
  font-size: 11px;
}

.history-change {
  padding: 11px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  font-size: 12px;
}

.history-change > strong {
  display: block;
  margin-bottom: 7px;
  color: #c9c9cd;
}

.history-change > div {
  display: flex;
  align-items: center;
  min-width: 0;
  flex-wrap: wrap;
  gap: 8px;
}

.history-change__old,
.history-change__new {
  max-width: 100%;
  padding: 5px 8px;
  border-radius: 8px;
  overflow-wrap: anywhere;
}

.history-change__old {
  color: #f5a0a7;
  background: rgba(220, 58, 70, 0.11);
}

.history-change__new {
  color: #72d697;
  background: rgba(0, 150, 64, 0.1);
}

.comment-list {
  display: grid;
  gap: 10px;
}

.comment-card {
  padding: 14px 15px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 14px !important;
  color: #dddddf;
  background: rgba(0, 0, 0, 0.15) !important;
}

.comment-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 7px;
}

.comment-card__header strong {
  font-size: 13px;
}

.comment-card__header span {
  color: #8e8e93;
  font-size: 11px;
  white-space: nowrap;
}

.comment-card__copy {
  color: #cacace;
  font-size: 13px;
  line-height: 1.55;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.empty-comments {
  margin: 2px 0 14px;
  color: #8e8e93;
  font-size: 13px;
}

.comment-input {
  margin-top: 14px !important;
}

.comment-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

.comment-actions .v-btn {
  min-height: 44px;
  border-radius: 12px;
  color: #ffffff !important;
  background: var(--pr-primary) !important;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.personal-requests-page ::v-deep .v-progress-linear.primary--text,
.app-dialog-card ::v-deep .v-progress-linear.primary--text,
.app-dialog-card ::v-deep .v-input--selection-controls__input .primary--text {
  color: var(--pr-primary) !important;
  caret-color: var(--pr-primary) !important;
}

.personal-requests-page ::v-deep .v-btn:focus-visible,
.personal-requests-page ::v-deep .v-input:focus-within,
.app-dialog-card ::v-deep .v-btn:focus-visible,
.app-dialog-card ::v-deep .v-input:focus-within {
  outline: none;
}

.personal-requests-page ::v-deep .v-btn:focus-visible,
.app-dialog-card ::v-deep .v-btn:focus-visible {
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.28) !important;
}

@media (max-width: 959px) {
  .personal-requests-page {
    padding: 18px;
  }

  .page-hero {
    min-height: 140px;
  }

  .app-dialog-card {
    border: 0 !important;
    border-radius: 0 !important;
  }

  .dialog-scroll-area {
    max-height: none;
  }

  .detail-dialog-header {
    flex-wrap: wrap;
  }

  .detail-dialog-header .dialog-header__copy {
    width: 100%;
  }

  .dialog-header__actions {
    width: 100%;
    justify-content: flex-start;
    padding-left: 64px;
  }

  .dialog-header__actions .touch-action:last-child {
    margin-left: auto;
  }

  .status-toggle {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 599px) {
  .personal-requests-page {
    padding: 12px;
  }

  .page-hero {
    align-items: stretch;
    flex-direction: column;
    padding: 22px 18px;
    border-radius: 20px;
  }

  .page-hero__copy {
    align-items: flex-start;
    gap: 13px;
  }

  .page-hero__icon {
    width: 46px;
    height: 46px;
    border-radius: 14px;
  }

  .page-hero__icon .v-icon {
    font-size: 24px;
  }

  .page-hero h1 {
    font-size: 27px;
  }

  .page-hero p {
    font-size: 13px;
  }

  .hero-cta {
    width: 100%;
  }

  .summary-grid {
    margin-top: 10px;
  }

  .summary-card {
    min-height: 88px;
    padding: 15px;
    border-radius: 17px;
  }

  .summary-card__icon {
    width: 42px;
    height: 42px;
  }

  .summary-card strong {
    font-size: 24px;
  }

  .filter-surface {
    margin-top: 12px;
    padding: 17px;
    border-radius: 19px;
  }

  .surface-heading {
    align-items: flex-start;
    flex-direction: column;
    margin-bottom: 13px;
    gap: 9px;
  }

  .surface-heading__meta {
    width: 100%;
    justify-content: space-between;
  }

  .request-card__top {
    align-items: flex-start;
    flex-direction: column;
  }

  .request-card__pills {
    justify-content: flex-start;
  }

  .dialog-header {
    min-height: auto;
    padding: 17px 16px !important;
  }

  .dialog-header__copy {
    align-items: flex-start;
    gap: 11px;
  }

  .dialog-header__icon {
    width: 40px;
    height: 40px;
    border-radius: 13px;
  }

  .dialog-header h2 {
    font-size: 19px;
  }

  .dialog-header p {
    font-size: 12px;
  }

  .dialog-close {
    width: 44px !important;
    min-width: 44px !important;
    height: 44px !important;
  }

  .detail-dialog-header .dialog-header__copy {
    padding-right: 34px;
  }

  .detail-dialog-header .dialog-header__actions {
    padding-left: 51px;
  }

  .dialog-scroll-area {
    padding: 16px !important;
  }

  .form-stack {
    gap: 12px;
  }

  .form-section,
  .detail-panel {
    padding: 15px;
    border-radius: 16px;
  }

  .section-heading {
    margin-bottom: 14px;
  }

  .signing-banner__content {
    align-items: stretch;
    flex-direction: column;
  }

  .banner-action {
    width: 100%;
  }

  .detail-row {
    grid-template-columns: 1fr;
    gap: 3px;
  }

  .status-toggle {
    grid-template-columns: 1fr;
  }

  .status-toggle ::v-deep .v-btn {
    justify-content: flex-start;
  }

  .comment-card__header {
    align-items: flex-start;
    flex-direction: column;
    gap: 2px;
  }

  .comment-card__header span {
    white-space: normal;
  }

  .dialog-actions {
    min-height: auto;
    flex-wrap: wrap;
    padding: 11px 12px !important;
    gap: 7px;
  }

  .dialog-actions .v-spacer {
    display: none;
  }

  .dialog-actions .secondary-action,
  .dialog-actions .primary-dialog-action {
    flex: 1 1 auto;
  }

  .dialog-actions .primary-dialog-action {
    order: -1;
    width: 100%;
  }

  .detail-dialog-actions .danger-action {
    flex: 1 1 auto;
  }

  .detail-dialog-actions .secondary-action,
  .detail-dialog-actions .danger-action {
    min-width: calc(50% - 7px);
  }
}
</style>
